import React from 'react';

import { Button } from '@mui/material';
import { AutocompleteCDS, CheckboxCDS, TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import { MarkedAppFormsTreeFieldId } from 'app/services/autoLockForms/autoLockForms.fieldIds';
import _ from 'lodash';

import { UnderwritingTestIds } from '../Underwriting.testIds';

import { getBorrowerFieldName } from './hooks/useLoanDenialForm';
import { editActionStyles } from './LoanDenial.styles';

export const addFormToTableData = ({
  tableData,
  editData,
  form,
  handleChange,
  validationErrors,
  isFormChanged,
  onCancel,
  onLoanDenialReasonChange,
  filteredDenialReasonOptions,
  isDenialReasonSelected,
  borrowerOptions,
  isLoading,
}) =>
  tableData.map(item =>
    item.id === editData.id
      ? {
          id: item.id,
          name: {
            content: (
              <AutocompleteCDS
                required
                withPopupError
                data-field-id={MarkedAppFormsTreeFieldId.UNDERWRITING_NEW_DENIAL_REASON}
                dataTestId={UnderwritingTestIds.LOAN_DENIAL_FORM_REASON_SELECT}
                disablePortal={false}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
                name="name"
                options={filteredDenialReasonOptions}
                placeholder="Select one"
                value={form.name ?? null}
                onChange={onLoanDenialReasonChange}
              />
            ),
          },
          ...(!isDenialReasonSelected
            ? {}
            : {
                HMDACode: {
                  content: (
                    <TextFieldCDS
                      disabled
                      dataTestId={UnderwritingTestIds.LOAN_DENIAL_FORM_HMDA_CODE_INPUT}
                      name="HMDACode"
                      value={form.HMDACode ?? ''}
                    />
                  ),
                },
                isDenialDueToCredit: {
                  content: (
                    <CheckboxCDS
                      disabled
                      dataTestId={UnderwritingTestIds.LOAN_DENIAL_FORM_CREDIT_CHECKBOX}
                      name="isDenialDueToCredit"
                      value={form.isDenialDueToCredit}
                    />
                  ),
                },
                ...borrowerOptions.reduce((acc, __, i) => {
                  const fieldName = getBorrowerFieldName(i);

                  acc[`borrower${i}`] = {
                    content: (
                      <CheckboxCDS
                        // The tree's single "Borrower Selection" field is the first checkbox.
                        data-field-id={
                          i === 0
                            ? MarkedAppFormsTreeFieldId.UNDERWRITING_DENIAL_BORROWER_SELECTION
                            : null
                        }
                        dataTestId={UnderwritingTestIds.getLoanDenialFormBorrowerCheckboxId(i)}
                        disabled={
                          isLoading || !form.isDenialDueToCredit || borrowerOptions.length === 1
                        }
                        name={fieldName}
                        value={form[fieldName]}
                        onChange={handleChange}
                      />
                    ),
                  };

                  return acc;
                }, {}),
                isAffiliate: {
                  content: (
                    <CheckboxCDS
                      disabled
                      dataTestId={UnderwritingTestIds.LOAN_DENIAL_FORM_AFFILIATE_CHECKBOX}
                      name="isAffiliate"
                      value={form.isAffiliate}
                    />
                  ),
                },
              }),
          action: {
            content: (
              <div style={editActionStyles}>
                <Button
                  data-testid={UnderwritingTestIds.LOAN_DENIAL_FORM_CANCEL_BUTTON}
                  size="small"
                  variant="outlined"
                  onClick={onCancel}
                >
                  Cancel
                </Button>
                <Button
                  data-testid={UnderwritingTestIds.LOAN_DENIAL_FORM_SUBMIT_BUTTON}
                  disabled={!isFormChanged}
                  size="small"
                  type="submit"
                >
                  Save
                </Button>
              </div>
            ),
          },
          isAdded: editData.isAdded,
        }
      : item,
  );

export const createNewTableData = () => ({
  id: _.uniqueId(),
  isNewlyAdded: true,
  isAdded: true,
});

export const normalizeLoanDenialFormData = ({ form }) =>
  form.map(({ denialReason, selectedBorrowers }) => ({
    denialReasonId: denialReason.id,
    selectedBorrowers,
  }));
